package ui.app.assign.detail

import androidx.compose.runtime.*
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.input.TextFieldValue
import api.AppType
import apps.App
import apps.App.Quiz.Question.Type
import common.frontend.api.Io
import compose.assets.Svg
import compose.assets.svg.Check
import compose.assets.svg.State_machine
import compose.assets.svg.Text_long
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.plus
import org.jetbrains.compose.resources.StringResource
import org.ssoft.common.tools.nowDate
import provider.CommonDisplayProvider
import provider.LocalAccountProvider
import provider.LocalDisplayProvider
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.LocalNavigation
import tk.mallumo.compose.navigation.LocalNavigationArgs
import tk.mallumo.compose.navigation.Navigation
import tk.mallumo.compose.navigation.viewmodel.globalViewModel
import tk.mallumo.compose.navigation.viewmodel.viewModel
import tools.*
import ui.GlobalVM
import ui.widget.DropDownItem
import ui.widget.DropDownResItem
import kotlin.reflect.KMutableProperty0
import kotlin.time.Clock
import kotlin.time.Duration
import kotlin.time.Duration.Companion.hours
import kotlin.time.Duration.Companion.minutes


interface AppDetailScope {

    val vm: AppDetailVM
    val vmGlobal: GlobalVM
    val action: (AppDetailVM.Action) -> Unit

    val args: AppDetailArgs
    val nav: Navigation
    val login: Io.User
    val display: CommonDisplayProvider

    companion object {
        @Composable
        fun rememberScope(): AppDetailScope {
            val nav = LocalNavigation.current
            val account = LocalAccountProvider.current
            val args = LocalNavigationArgs.current.rememberArgs(AppDetailArgs::class)
            val vm = nav.viewModel(AppDetailVM::class, "${args.companyId}:${args.appId}")
            val vmGlobal = globalViewModel(GlobalVM::class)
            val action = AppDetailVM.Action.rememberAction(vm)
            val display = LocalDisplayProvider.current
            return remember {
                object : AppDetailScope {
                    override val vm: AppDetailVM
                        get() = vm
                    override val vmGlobal: GlobalVM
                        get() = vmGlobal
                    override val action: (AppDetailVM.Action) -> Unit
                        get() = action
                    override val args: AppDetailArgs
                        get() = args
                    override val nav: Navigation
                        get() = nav
                    override val login: Io.User
                        get() = account.current!!
                    override val display: CommonDisplayProvider
                        get() = display
                }.also {
                    vm.init(it)
                }
            }
        }
    }


    class BoardUpdateInterval(
        val value: Duration,
        override val label: String = value.toString(),
        override val image: ImageVector? = null
    ) : DropDownItem {
        companion object {
            val all by lazy {
                listOf(
                    BoardUpdateInterval(6.hours),
                    BoardUpdateInterval(12.hours),
                    BoardUpdateInterval(24.hours),
                    BoardUpdateInterval(48.hours),
                )
            }
        }
    }

    class Wrapper(
        val canEdit: Boolean,
        val canDelete: Boolean,
        override val data: Io.AppDetail,
        override val key: String = data.id.toString(),
        private val changeInternal: MutableState<Boolean> = mutableStateOf(false),
        val type: AppType = AppType[data.type],
        val locations: WrapperLocations = WrapperLocations(canEdit, data),
        val app: WrapperDefinition = WrapperDefinition.get(data),
    ) : EFieldList.Entry<Io.AppDetail> {

        var origin = data.copy()
            private set

        fun confirmChanges() {
            origin = data.copy()
            locations.confirmChanges()
            app.confirmChanges()
            changeInternal.value = false
        }

        val change by changeInternal

        val name = EFieldTextString(
            property = data::name,
            condition = { it.trim().takeIf { it.isNotEmpty() } },
            onChange = {
                makeChanges()
            }
        )

        val logo = EFieldRegularLong(
            property = data::logo,
            onChange = { makeChanges() }
        )

        val note = EFieldTextString(
            property = data::note,
            onChange = {
                makeChanges()
            }
        )

        val start = EFieldRegularDate(
            property = data::start,
            onChange = {
                makeChanges()
            }
        )

        val end = EFieldRegularDate(
            property = data::end,
            onChange = {
                makeChanges()
            }
        )

        val scheduled = EFieldRegularBoolean(
            property = data::scheduled,
            onChange = {
                if (it) {
                    start.set(Clock.nowDate())
                    end.set(Clock.nowDate().plus(1, DateTimeUnit.MONTH))
                }
                makeChanges()
            }
        )

        private fun makeChanges() {
            changeInternal.value = when {
                name.err -> false
                origin.logo != data.logo -> true
                origin.name != data.name -> true
                origin.note != data.note -> true
                origin.start != data.start -> true
                origin.end != data.end -> true
                origin.scheduled != data.scheduled -> true
                else -> false
            }
        }

    }

    sealed class WrapperDefinition(val containsSecondaryPanel: Boolean) {
        var changed by mutableStateOf(false)
            protected set

        abstract fun makeChange()

        open fun confirmChanges() = Unit

        companion object {

            fun get(appDetail: Io.AppDetail): WrapperDefinition {
                return when (AppType[appDetail.type]) {
                    AppType.APP_GROUP -> OfAppGroup(appDetail)
                    else -> when (val app = appDetail.data) {
                        is App.Rss -> OfRss(app)
                        is App.Image -> OfImage(app)
                        is App.Pdf -> OfPdf(app)
                        is App.Video -> OfVideo(app)
                        is App.VideoAlphaLoop -> OfVideoAlphaLoop(app)
                        is App.WwwEmbedded -> OfWwwEmbedded(app)
                        is App.WwwLink -> OfWwwLink(app)
                        is App.Balloon -> OfBalloon(app)
                        is App.Quiz -> OfQuiz(app)
                        is App.Gallery -> OfGallery(app)
                        is App.Camera -> OfCamera(app)
                        is App.Maze -> OfMaze(app)
                        is App.UsbPlayer -> OfUsbPlayer(app)
                        is App.None,
                        is App.Wheel,
                        is App.Kindergarten,
                        is App.AppGroup -> OfNone(app)
                    }
                }
            }
        }

        class OfNone(val app: App) : WrapperDefinition(false) {
            override fun makeChange() = Unit
        }

        class OfCamera(val data: App.Camera) : WrapperDefinition(false) {
            override fun makeChange() = Unit
        }

        class OfUsbPlayer(val data: App.UsbPlayer) : WrapperDefinition(false) {

            override fun makeChange() {
                changed = allFields.any { it.change }
            }

            override fun confirmChanges() {
                allFields.forEach { it.applyChanges() }
                super.confirmChanges()
            }

            val allFields
                get() = listOf(
                    title,
                    showImage,
                    showVideo,
                    autoContinue,
                    showMediaName,
                    sortBy,
                    scaleAs
                )
            val title = EFieldTextString(
                property = data.content::title,
                onChange = {
                    makeChange()
                }
            )
            val showImage = EFieldRegularBoolean(
                property = data.content::showImage,
                onChange = {
                    makeChange()
                }
            )
            val showVideo = EFieldRegularBoolean(
                property = data.content::showVideo,
                onChange = {
                    makeChange()
                }
            )
            val autoContinue = EFieldRegularBoolean(
                property = data.content::autoContinue,
                onChange = {
                    makeChange()
                }
            )
            val showMediaName = EFieldRegularBoolean(
                property = data.content::showMediaName,
                onChange = {
                    makeChange()
                }
            )
            val sortBy = EFieldRegularInt(
                property = data.content::sort,
                onChange = {
                    makeChange()
                }
            )
            val scaleAs = EFieldRegularInt(
                property = data.content::scale,
                onChange = {
                    makeChange()
                }
            )

        }

        class OfMaze(val data: App.Maze) : WrapperDefinition(false) {

            val splashPort = EFieldRegularLong(
                property = data.content::splashPort,
                onChange = {
                    makeChange()
                }
            )

            val splashLand = EFieldRegularLong(
                property = data.content::splashLand,
                onChange = {
                    makeChange()
                }
            )
            val scale = EFieldRegularFloat(
                property = data.content::scale,
                onChange = {
                    makeChange()
                }
            )
            val title = EFieldTextString(
                property = data.content::title,
                onChange = {
                    makeChange()
                }
            )

            val timerSplash = EFieldDualLongAsFloat(
                property = data.content.timer::splash,
                maxValue = 5.minutes.inWholeMilliseconds,
                onChange = {
                    makeChange()
                }
            )

            val timerGame = EFieldDualLongAsFloat(
                property = data.content.timer::game,
                maxValue = 5.minutes.inWholeMilliseconds,
                onChange = {
                    makeChange()
                }
            )

            val timerResult = EFieldDualLongAsFloat(
                property = data.content.timer::result,
                maxValue = 5.minutes.inWholeMilliseconds,
                onChange = {
                    makeChange()
                }
            )

            private val allFields by lazy {
                listOf(
                    scale,
                    splashPort, splashLand, title,
                    timerSplash, timerGame, timerResult
                )
            }

            override fun makeChange() {
                changed = allFields.any { it.change }
            }

            override fun confirmChanges() {
                allFields.forEach { it.applyChanges() }
                super.confirmChanges()
            }
        }

        class OfImage(val data: App.Image) : WrapperDefinition(false) {

            private var origin = data.copy(
                content = data.content.copy()
            )

            val image = EFieldRegularLong(
                property = data.content::id,
                onChange = {
                    makeChange()
                }
            )

            override fun makeChange() {
                changed = when {
                    image.state != origin.content.id -> true
                    else -> false
                }
            }
        }

        class OfBalloon(val data: App.Balloon) : WrapperDefinition(false) {

            private var origin = data.copy(
                content = data.content.copy()
            )


            val levelDuration = EFieldRegularInt(
                property = data.content::levelDuration,
                onChange = {
                    makeChange()
                }
            )
            val levelSpawn = EFieldRegularInt(
                property = data.content::levelSpawn,
                onChange = {
                    makeChange()
                }
            )

            val sizePct = EFieldRegularFloat(
                property = data.content::sizePct,
                onChange = {
                    makeChange()
                }
            )

            val count = EFieldTextInt(
                property = data.content::count,
                onChange = {
                    makeChange()
                }
            )

            override fun makeChange() {
                changed = when {
                    levelDuration.state != origin.content.levelDuration -> true
                    levelSpawn.state != origin.content.levelSpawn -> true
                    sizePct.state != origin.content.sizePct -> true
                    count.state != origin.content.count -> true
                    else -> false
                }
            }
        }

        class OfQuiz(val data: App.Quiz) : WrapperDefinition(true) {

            var questions = Question(data) {
                makeChange()
            }
                private set

            private var origin = data.copy(
                content = data.content.copy(
                    translations = data.content.translations.copy()
                )
            )

            override fun confirmChanges() {
                origin = data.copy(
                    content = data.content.copy(
                        translations = data.content.translations.copy()
                    )
                )
                questions = Question(data) {
                    makeChange()
                }
                changed = false
            }

            val backgroundImage = EFieldRegularLong(
                property = data.content::backgroundImage,
                onChange = {
                    makeChange()
                }
            )


            val resultTitle = EFieldTextString(
                property = data.content.translations::resultTitle,
                onChange = {
                    makeChange()
                }
            )

            val resultMsg = EFieldTextString(
                property = data.content.translations::resultMsg,
                onChange = {
                    makeChange()
                }
            )

            class Question(
                val data: App.Quiz,
                onChange: EFieldList<App.Quiz.Question, Item>.() -> Unit
            ) : EFieldList<App.Quiz.Question, Question.Item>(
                items = data.content.questionsStack.questions.apply {
                    sortBy { it.position }
                },
                onChange = {
                    onChange()
                },
                onBindEntry = {
                    Item(it) {
                        onChange()
                    }
                }
            ) {
                private val origin = data.content.questionsStack.let { s ->
                    s.copy(
                        questions = s.questions.map { q ->
                            q.copy(
                                answers = q.answers.map { a ->
                                    a.copy()
                                }.toMutableList()
                            )
                        }.toMutableList()
                    )
                }

                fun changed(): Boolean {
                    if (origin.questions.size != state.size) return true
                    return origin.questions.indices.any { qIndex ->
                        val old = origin.questions[qIndex]
                        val new = state[qIndex].data
                        old != new
                                || old.answers.size != new.answers.size
                                || old.answers.indices.any { aIndex ->
                            old.answers[aIndex] != new.answers[aIndex]
                        }
                    }
                }

                class Type(
                    val type: App.Quiz.Question.Type,
                    override val label: StringResource = when (type) {
                        App.Quiz.Question.Type.MULTI -> Res.string.layer_bottom_detail__quiz_answer_type_multi
                        App.Quiz.Question.Type.SINGLE -> Res.string.layer_bottom_detail__quiz_answer_type_single
                        App.Quiz.Question.Type.TEXT -> Res.string.layer_bottom_detail__quiz_answer_type_text
                    },
                    override val image: ImageVector? = App.Quiz.Question.Type.icon(type.type)
                ) : DropDownResItem

                companion object {
                    val types by lazy {
                        App.Quiz.Question.Type.all.map { Type(it) }
                    }
                }


                class Item(
                    override val data: App.Quiz.Question,
                    override val key: String = data.key.toString(),
                    val onMakeChange: () -> Unit
                ) : Entry<App.Quiz.Question> {

                    val questionValue = EFieldTextString(
                        property = data::value,
                        condition = { it.takeIf { it.trim().isNotEmpty() } },
                        onChange = {
                            onMakeChange()
                        }
                    )

                    val position = EFieldRegularInt(
                        property = data::position,
                        onChange = {
                            onMakeChange()
                        }
                    )

                    val type = EFieldRegularInt(
                        property = data::type,
                        onChange = {
                            onMakeChange()
                        }
                    )

                    var answers = Answer(data) {
                        onMakeChange()
                    }
                }
            }


            class Answer(
                val data: App.Quiz.Question,
                onChange: EFieldList<App.Quiz.Answer, Item>.() -> Unit
            ) : EFieldList<App.Quiz.Answer, Answer.Item>(
                items = data.answers,
                onChange = {
                    onChange()
                },
                onBindEntry = {
                    Item(it) {
                        onChange()
                    }
                }
            ) {
                class Item(
                    override val data: App.Quiz.Answer,
                    override val key: String = data.key.toString(),
                    val focusRequester: FocusRequester = FocusRequester(),
                    val onMakeChange: () -> Unit
                ) : Entry<App.Quiz.Answer> {

                    val answerValue = EFieldTextString(
                        property = data::value,
                        onChange = {
                            onMakeChange()
                        }
                    )
                }
            }

            suspend fun setup() {
                if (resultTitle.state.isEmpty()) {
                    resultTitle.set(TextFieldValue(Res.string.layer_bottom_detail__quiz_field_result_title.await()))
                }
                if (resultMsg.state.isEmpty()) {
                    resultMsg.set(TextFieldValue(Res.string.layer_bottom_detail__quiz_field_result_msg.await()))
                }
                setupStaticTexts(
                    data.content.translations::tooltipAnswerSingle to Res.string.layer_bottom_detail__quiz_field_tooltip_answer_single,
                    data.content.translations::tooltipAnswerMulti to Res.string.layer_bottom_detail__quiz_field_tooltip_answer_multi,
                    data.content.translations::tooltipAnswerText to Res.string.layer_bottom_detail__quiz_field_tooltip_answer_text,
                    data.content.translations::buttonNext to Res.string.layer_bottom_detail__quiz_field_button_next,
                    data.content.translations::buttonSend to Res.string.layer_bottom_detail__quiz_field_button_send,
                    data.content.translations::labelQuestionOf to Res.string.layer_bottom_detail__quiz_field_tooltip_question_of,
                )
            }

            private suspend fun setupStaticTexts(vararg pair: Pair<KMutableProperty0<String>, StringResource>) {
                pair.filter { it.first.get().isEmpty() }
                    .forEach { it.first.set(it.second.await()) }
            }

            override fun makeChange() {
                changed = when {
                    resultMsg.err -> false
                    resultTitle.err -> false

                    resultTitle.state != origin.content.translations.resultTitle -> true
                    resultMsg.state != origin.content.translations.resultMsg -> true

                    backgroundImage.state != origin.content.backgroundImage -> true

                    questions.changed() -> true
                    else -> false
                }
            }
        }

        class OfGallery(val data: App.Gallery) : WrapperDefinition(true) {

            var media = Media(data) {
                makeChange()
            }
                private set

            private var origin = data.copy(
                content = data.content.copy()
            )

            val volume = EFieldRegularFloat(
                property = data.content::volume,
                onChange = {
                    makeChange()
                }
            )
            val volumeMax = EFieldRegularFloat(
                property = data.content::volumeMax,
                onChange = {
                    makeChange()
                }
            )

            override fun confirmChanges() {
                origin = data.copy(
                    content = data.content.copy()
                )
                media = Media(data) {
                    makeChange()
                }
                changed = false
            }


            class Media(
                val data: App.Gallery,
                onChange: EFieldList<App.Gallery.Media, Item>.() -> Unit
            ) : EFieldList<App.Gallery.Media, Media.Item>(
                items = data.content.media.apply {
                    sortBy { it.position }
                },
                onChange = {
                    onChange()
                },
                onBindEntry = {
                    Item(it)
                }
            ) {
                private val origin = data.content.media.map {
                    when (it) {
                        is App.Gallery.Media.Image -> it.copy()
                        is App.Gallery.Media.Video -> it.copy()
                    }
                }

                fun changed(): Boolean {
                    if (origin.size != state.size) return true
                    return origin.indices.any { qIndex ->
                        origin[qIndex].id != state[qIndex].data.id
                    }
                }

                class Item(
                    override val data: App.Gallery.Media,
                    override val key: String = data.id.toString(),
                ) : Entry<App.Gallery.Media>
            }


            override fun makeChange() {
                changed = when {
                    media.changed() -> true
                    volume.state != origin.content.volume -> true
                    volumeMax.state != origin.content.volumeMax -> true
                    else -> false
                }
            }
        }

        class OfRss(val data: App.Rss) : WrapperDefinition(false) {
            override fun makeChange() = Unit
        }

        class OfVideo(val data: App.Video) : WrapperDefinition(false) {
            private var origin = data.copy(
                content = data.content.copy()
            )

            val video = EFieldRegularLong(
                property = data.content::id,
                onChange = {
                    makeChange()
                }
            )

            val volume = EFieldRegularFloat(
                property = data.content::volume,
                onChange = {
                    makeChange()
                }
            )
            val volumeMax = EFieldRegularFloat(
                property = data.content::volumeMax,
                onChange = {
                    makeChange()
                }
            )

            override fun makeChange() {
                changed = when {
                    video.state != origin.content.id -> true
                    volume.state != origin.content.volume -> true
                    volumeMax.state != origin.content.volumeMax -> true
                    else -> false
                }
            }
        }

        class OfVideoAlphaLoop(val data: App.VideoAlphaLoop) : WrapperDefinition(false) {
            private var origin = data.copy(
                content = data.content.copy()
            )

            val video = EFieldRegularLong(
                property = data.content::id,
                onChange = {
                    makeChange()
                }
            )

            val repeats = EFieldRegularInt(
                property = data.content::repeats,
                onChange = {
                    makeChange()
                }
            )
            val delayStart = EFieldRegularLong(
                property = data.content::delayStart,
                onChange = {
                    makeChange()
                }
            )
            val delayEnd = EFieldRegularLong(
                property = data.content::delayEnd,
                onChange = {
                    makeChange()
                }
            )

            override fun makeChange() {
                changed = when {
                    video.state != origin.content.id -> true
                    repeats.state != origin.content.repeats -> true
                    delayStart.state != origin.content.delayStart -> true
                    delayEnd.state != origin.content.delayEnd -> true
                    else -> false
                }
            }
        }

        class OfPdf(val data: App.Pdf) : WrapperDefinition(false) {

            val pdf = EFieldRegularLong(
                property = data.content::id,
                onChange = {
                    makeChange()
                }
            )

            val pagingMode = EFieldRegularInt(
                property = data.content::pagingMode,
                onChange = {
                    makeChange()
                }
            )
            private val allFields get() = listOf(pdf, pagingMode)

            override fun makeChange() {
                changed = allFields.any { it.change }
            }
        }

        class OfAppGroup(
            val data: Io.AppDetail,
            val app: App.AppGroup = data.data as App.AppGroup
        ) : WrapperDefinition(true) {

            private var originItems = data.nodes.map { it.copy() }
            private var originApp = app.content.copy()

            val labelSearch = EFieldTextString(
                property = app.content::labelSearch,
                condition = { it.trim().takeIf { it.isNotEmpty() } },
                onChange = {
                    makeChange()
                }
            )

            val showImages = EFieldRegularBoolean(
                property = app.content::showImages,
                onChange = {
                    makeChange()
                }
            )

            override fun confirmChanges() {
                originItems = apps.state.map { it.data.copy() }
                originApp = app.content.copy()
                changed = false
            }

            override fun makeChange() {
                changed = labelSearch.state != originApp.labelSearch ||
                        showImages.state != originApp.showImages ||
                        apps.state.mapIndexed { index, appNode -> originItems[index] != appNode.data }.any { it }
            }


            val apps = Apps(data) {
                val selected = state.filter { it.enabled.state }
                state.forEach { item ->
                    item.position.set(
                        selected.indexOf(item)
                            .takeIf { it > -1 }
                            ?: Int.MAX_VALUE)
                }
                makeChange()
            }

            class Apps(
                val data: Io.AppDetail,
                onChange: EFieldList<Io.AppDetail.AppNode, Item>.() -> Unit
            ) : EFieldList<Io.AppDetail.AppNode, Apps.Item>(
                items = data.nodes.apply {

                    val selected = filter { it.active }
                        .sortedBy { it.position }

                    val nonSelected = filter { !it.active }
                        .sortedByDescending { it.id }

                    forEach { item ->
                        item.position = selected.indexOf(item)
                            .takeIf { it > -1 }
                            ?: run {
                                nonSelected.indexOf(item) + selected.size
                            }
                    }

                    sortBy { it.position }
                },
                onChange = {
                    onChange()
                },
                onBindEntry = {
                    Item(it) {
                        onChange()
                    }
                }
            ) {

                class Item(
                    override val data: Io.AppDetail.AppNode,
                    override val key: String = data.id.toString(),
                    val onMakeChange: () -> Unit
                ) : Entry<Io.AppDetail.AppNode> {

                    val enabled = EFieldRegularBoolean(
                        property = data::active,
                        onChange = {
                            onMakeChange()
                        }
                    )
                    val position = EFieldRegularInt(
                        property = data::position,
                    )
                }
            }


        }

        class OfWwwLink(val data: App.WwwLink) : WrapperDefinition(false) {
            private val originSitesCount = data.content.enabledSites.count()

            val sites = WrapperEnabledSite(data) {
                makeChange()
            }

            private var origin = data.copy(
                content = data.content.copy()
            )

            val url = EFieldTextValue(
                property = data.content::url,
                condition = { it.trim().takeIf { it.startsWith("http://") || it.startsWith("https://") } },
                onChange = {
                    makeChange()
                }
            )

            val keyboard = EFieldRegularBoolean(
                property = data.content::keyboard,
                onChange = {
                    makeChange()
                }
            )

            val touchDragMode = EFieldRegularInt(
                property = data.content::touchDragMode,
                onChange = {
                    makeChange()
                }
            )

            val fullscreen = EFieldRegularBoolean(
                property = data.content::fullscreen,
                onChange = {
                    makeChange()
                }
            )

            val controlsPanel = EFieldRegularBoolean(
                property = data.content::controlsPanel,
                onChange = {
                    makeChange()
                }
            )

            val blockOtherSites = EFieldRegularBoolean(
                property = data.content::blockOtherSites,
                onChange = {
                    makeChange()
                }
            )

            val zoomDefault = EFieldRegularInt(
                property = data.content::zoomDefault,
                onChange = {
                    makeChange()
                }
            )

            val pageScale = EFieldRegularFloat(
                property = data.content::pageScale,
                onChange = {
                    makeChange()
                }
            )

            override fun makeChange() {
                changed = when {
                    url.err -> false
                    blockOtherSites.state && sites.state.any { it.url.err } -> false
                    url.state != origin.content.url -> true
                    keyboard.state != origin.content.keyboard -> true
                    fullscreen.state != origin.content.fullscreen -> true
                    controlsPanel.state != origin.content.controlsPanel -> true
                    blockOtherSites.state != origin.content.blockOtherSites -> true
                    zoomDefault.state != origin.content.zoomDefault -> true
                    pageScale.state != origin.content.pageScale -> true
                    touchDragMode.state != origin.content.touchDragMode -> true
                    sites.state.count() != originSitesCount -> true
                    sites.state.any { it.change } -> true
                    else -> false
                }
            }


            class WrapperEnabledSite(
                app: App.WwwLink,
                onChange: () -> Unit
            ) : EFieldList<App.WwwLink.EnabledSite, WrapperEnabledSite.Item>(
                items = app.content.enabledSites,
                onBindEntry = {
                    Item(it) {
                        onChange()
                    }
                },
                onChange = { onChange() }
            ) {
                class Item(
                    override val data: App.WwwLink.EnabledSite,
                    override val key: String = data.id.toString(),
                    private val onChange: () -> Unit,
                ) : Entry<App.WwwLink.EnabledSite> {
                    private val origin = data.copy()
                    var change by mutableStateOf(false)
                        private set
                    val url = EFieldTextValue(
                        property = data::url,
                        condition = { it.trim().takeIf { it.startsWith("http://") || it.startsWith("https://") } },
                        onChange = {
                            change = origin.url != it
                            onChange()
                        }
                    )
                }
            }
        }

        class OfWwwEmbedded(val data: App.WwwEmbedded) : WrapperDefinition(false) {
            private var origin = data.copy(
                content = data.content.copy()
            )

            val zip = EFieldRegularLong(
                property = data.content::id,
                onChange = {
                    makeChange()
                }
            )
            val name = EFieldRegularString(
                property = data.content::name,
                onChange = {
                    makeChange()
                }
            )

            val keyboard = EFieldRegularBoolean(
                property = data.content::keyboard,
                onChange = {
                    makeChange()
                }
            )

            override fun makeChange() {
                changed = when {
                    zip.state != origin.content.id -> true
                    name.state != origin.content.name -> true
                    keyboard.state != origin.content.keyboard -> true
                    else -> false
                }
            }
        }
    }

    class WrapperLocations(
        val canEdit: Boolean,
        app: Io.AppDetail,
    ) : EFieldList<Io.AppDetail.LocationItem, WrapperLocations.Item>(
        items = app.locations,
        onBindEntry = { Item(it, canEdit = canEdit) }
    ) {
        fun confirmChanges() {
            state.forEach {
                it.confirmChanges()
            }
        }

        class Item(
            override val data: Io.AppDetail.LocationItem,
            override val key: String = data.id.toString(),
            canEdit: Boolean
        ) : Entry<Io.AppDetail.LocationItem> {
            private val initialSelectionState = generateSelectionState()

            var selectionState by mutableStateOf(initialSelectionState)
                private set

            val devices = WrapperLocationDevice(canEdit, data) {
                selectionState = generateSelectionState()
                makeChange()
            }

            private fun makeChange() {
                change = devices.state.any { it.changed }
            }

            var change by mutableStateOf(false)
                private set

            fun confirmChanges() {
                change = false
                devices.state.forEach {
                    it.confirmChanges()
                }
            }

            enum class SelectionState {
                EMPTY, NONE, ALL, PARTIAL
            }

            private fun generateSelectionState(): SelectionState {
                val all = data.devices.size
                val selected = data.devices.count { it.enabled }
                val notSelected = data.devices.count { !it.enabled }
                return when {
                    all == 0 -> SelectionState.EMPTY
                    selected == 0 -> SelectionState.NONE
                    data.devices.isEmpty() -> SelectionState.NONE
                    all == selected -> SelectionState.ALL
                    notSelected != 0 -> SelectionState.PARTIAL
                    else -> SelectionState.NONE
                }
            }
        }
    }

    class WrapperLocationDevice(
        val canEdit: Boolean,
        location: Io.AppDetail.LocationItem,
        val onItemChange: () -> Unit,
    ) : EFieldList<Io.AppDetail.DeviceItem, WrapperLocationDevice.Item>(
        items = location.devices,
        onBindEntry = { Item(it, onChange = onItemChange) }
    ) {

        class Item(
            override val data: Io.AppDetail.DeviceItem,
            override val key: String = data.id.toString(),
            val onChange: () -> Unit,
        ) : Entry<Io.AppDetail.DeviceItem> {

            fun confirmChanges() {
                origin = data.copy()
                changed = false
            }

            private var origin = data.copy()

            var changed by mutableStateOf(false)
                private set

            val enabled = EFieldRegularBoolean(
                property = data::enabled,
                onChange = {
                    changed = origin.enabled != data.enabled
                    onChange()
                })
        }
    }
}


fun Type.Companion.icon(type: Int): ImageVector {
    return when (Type[type]) {
        Type.MULTI -> Svg.State_machine
        Type.SINGLE -> Svg.Check
        Type.TEXT -> Svg.Text_long
    }
}
